import React, { useState, useEffect } from "react";
import _ from "lodash";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import LanguageSwitcher from "Jarvis/JarvisComponents/LanguageSwitcher";
import { RetrievePublic } from "Jarvis/JarvisSystems/Pub/Services/PageElementService";
import Wait from "Jarvis/JarvisComponents/Wait";
import Typography from "Jarvis/JarvisComponents/Typography";
import FormContainer from "./FormContainer";
import SignInUserInfo from "./SignInUserInfo";
import ConcurrentLogin from "./ConcurrentLogin";
import AccountModern from "./AccountModern";

const DESIGN_STORAGE_KEY = "jarvisLoginDesign";
const getInitialDesign = () => {
  try {
    const v = localStorage.getItem(DESIGN_STORAGE_KEY);
    return v === "classic" ? "classic" : "modern";
  } catch {
    return "modern";
  }
};

const Account = (props) => {
  const { language, isInternalSignIn = false } = props;
  const [design, setDesign] = useState(getInitialDesign);
  const switchDesign = (next) => {
    setDesign(next);
    try {
      localStorage.setItem(DESIGN_STORAGE_KEY, next);
    } catch {}
  };
  const [stateObj, setStateObj] = useState({
    backgroundImageName: null,
    loginResultInfo: null,
    response: null,
    errorMessage: "",
    concurrentLogins: [],
  });
  const backgroundImageCount = 8;

  useEffect(() => {
    (async () => {
      const { ErrorHandling, Data } = await RetrievePublic({
        langId: language.LangId,
        moduleCodeStrings: "SecAuthentication",
        configs: "PubCompanyName",
      });
      let response = null;
      let errorMessage = null;
      if (_.get(ErrorHandling, "IsSuccessful")) response = Data;
      else errorMessage = _.get(ErrorHandling, "ErrorMessage");
      setStateObj({
        ...stateObj,
        response: response,
        errorMessage: errorMessage,
        backgroundImageName:
          "0" + Math.ceil(Math.random() * backgroundImageCount),
      });
    })();
  }, [language.LangId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stateObj?.response)
    return (
      <div className="app-main-container">
        <Wait />
      </div>
    );

  if (stateObj.errorMessage)
    return (
      <pre style={{ wordBreak: "break-word" }}>
        {JSON.stringify(stateObj.errorMessage, null, 2)}
      </pre>
    );

  const langData = _.get(stateObj.response, "PE");
  const configs = _.get(stateObj.response, "Configs");

  const _SetLoginResultInfo = (data) => {
    setStateObj({
      ...stateObj,
      concurrentLogins: _.get(data, "Data.ConcurrentLogins") ?? [],
      loginResultInfo: data,
    });
  };

  const onLoadConcurrentLogins = (data) => {
    setStateObj({ ...stateObj, concurrentLogins: data ?? [] });
  };

  const switchWrapStyle = {
    position: "fixed",
    top: 16,
    right: 16,
    zIndex: 50,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: 6,
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.55)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.18)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
  };
  const switchBtnStyle = (active) => ({
    appearance: "none",
    border: 0,
    background: active
      ? "linear-gradient(135deg, #5e72e4, #825ee4)"
      : "transparent",
    color: active ? "#fff" : "rgba(255,255,255,0.75)",
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: 999,
    cursor: "pointer",
    letterSpacing: "0.03em",
    boxShadow: active ? "0 4px 12px rgba(94,114,228,0.45)" : "none",
    transition: "background 0.2s, color 0.2s",
  });
  const designSwitcher = isInternalSignIn ? null : (
    <div style={switchWrapStyle} role="group" aria-label="Login design">
      <button
        type="button"
        style={switchBtnStyle(design === "modern")}
        onClick={() => switchDesign("modern")}
        title="Modern design"
      >
        Modern
      </button>
      <button
        type="button"
        style={switchBtnStyle(design === "classic")}
        onClick={() => switchDesign("classic")}
        title="Classic design"
      >
        Classic
      </button>
    </div>
  );

  if (design === "modern") {
    return (
      <>
        {designSwitcher}
        <AccountModern
          {...props}
          isInternalSignIn={isInternalSignIn}
          langData={langData}
          configs={configs}
          loginResultInfo={stateObj.loginResultInfo}
          concurrentLogins={stateObj.concurrentLogins}
          _SetLoginResultInfo={_SetLoginResultInfo}
          onLoadConcurrentLogins={onLoadConcurrentLogins}
        />
      </>
    );
  }

  if (stateObj.loginResultInfo) {
    return (
      <div
        className="login-full-page"
        style={{
          backgroundImage:
            "url(" +
            require("assets/images/login/" +
              stateObj.backgroundImageName +
              ".jpg") +
            ")",
        }}
      >
        {designSwitcher}
        <SignInUserInfo
          langData={langData}
          data={stateObj.loginResultInfo}
          isInternalSignIn={isInternalSignIn}
          concurrentLogins={stateObj.concurrentLogins}
          {...props}
        />
      </div>
    );
  }

  return (
    <div
      className="login-full-page"
      style={{
        backgroundImage:
          "url(" +
          require("assets/images/login/" +
            stateObj.backgroundImageName +
            ".jpg") +
          ")",
      }}
    >
      {designSwitcher}
      {stateObj.concurrentLogins.length > 0 ? (
        <div
          style={{ maxWidth: "none" }}
          className="app-login-container d-flex justify-content-center align-items-center animated slideInUpTiny animation-duration-3"
        >
          <div className="app-login-main-content">
            <ConcurrentLogin
              concurrentLogins={stateObj.concurrentLogins}
              onLoadConcurrentLogins={onLoadConcurrentLogins}
              isBackToLoginNeeded={true}
            />
          </div>
        </div>
      ) : (
        <div className="app-login-container d-flex justify-content-center align-items-center animated slideInUpTiny animation-duration-3">
          <div className="app-login-main-content">
            <div className="app-logo-content">
              {isInternalSignIn === false && (
                <div className="d-flex align-items-center justify-content-center">
                  <LanguageSwitcher
                    withBorder={true}
                    accessMustBeChecked={false}
                    {...props}
                  />
                </div>
              )}
              <div className="d-flex align-items-center justify-content-center container-fluid">
                <div className="row">
                  <div className="col-md-12">
                    <Link className="logo-lg" to="/" title="Jarvis">
                      <img
                        src={require("assets/images/logo.png")}
                        alt="Jarvis"
                        title="Jarvis"
                        width="100%"
                        height="100%"
                      />
                    </Link>
                  </div>
                  <div
                    className="d-flex align-items-center justify-content-center col-md-12"
                    style={{ color: "white" }}
                  >
                    <Typography label={_.get(configs, "PubCompanyName")} />
                  </div>
                </div>
              </div>
            </div>
            <div className="app-login-content">
              <div className="app-login-header mb-4">
                <Typography
                  label={_.get(langData, "SecAuthentication_Title")}
                  variant="h6"
                />
              </div>
              <div>
                <FormContainer
                  langData={langData}
                  configs={configs}
                  isInternalSignIn={isInternalSignIn}
                  _SetLoginResultInfo={_SetLoginResultInfo}
                  onLoadConcurrentLogins={onLoadConcurrentLogins}
                  {...props}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ settings }) => {
  const { language } = settings;
  return { language };
};

export default connect(mapStateToProps)(Account);
